import React, { useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';

import { CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { getLogger } from 'app-components/utils/core';

import { billPresentmentAPI } from 'app-components/flux/bill-presentment';
import { useBillersWithLogos } from 'app-components/flux/bill-presentment/billPresentmentHooks';
import type { Biller } from 'app-components/flux/bill-presentment/billPresentmentTypes';

import DefaultLogo from 'assets/illustrations/Default_biller_logo.svg';
import Dump from 'components/Dump';
import ZeroStateView from 'components/ZeroStateView';
import fallbackBillers from './fallbackBillers';

const useStyles = makeStyles()((theme) => ({
  billerSpace: {
    display: 'grid',
    gap: '12px',
    gridTemplateColumns: 'repeat(4, 1fr)', // 4 equal columns
    gridTemplateRows: 'repeat(4, auto)', // 4 rows
    justifyContent: 'center',
    alignItems: 'center',
  },
  centered: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    minHeight: 250,
  },
  billerButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    height: '100%',
    minHeight: 64,
    width: '100%',
    minWidth: 120,
    padding: 8,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    border: `1px solid transparent`,
    boxShadow: theme.shadows[1],
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[5],
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
  logoImage: {
    border: `1px solid ${theme.palette.border.light}`,
    width: 48,
    objectFit: 'contain',
    borderRadius: 12,
  },

  logoText: {
    marginLeft: 8,
    textAlign: 'left',
    display: '-webkit-box',
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2, // limits to 2 lines
    lineHeight: '1.3em',
    maxHeight: '2.6em',
    textOverflow: 'ellipsis',
  },
}));

// Create a type for the props of the component
interface PopularBillersGridProps {
  handleSelect: (biller: Biller) => void;
}

const log = getLogger('BillPresentment/PopularBillersGrid');

export const PopularBillersGrid = React.memo(({ handleSelect }: PopularBillersGridProps) => {
  const { classes } = useStyles();
  const MAX_BILLERS = 16; // Limit to 16 billers
  const [billers, setBillers] = React.useState<Biller[]>([]);
  const [hasFetched, setHasFetched] = React.useState(false);

  const getPopularBillers = async () => {
    try {
      const response = await billPresentmentAPI.getPopularBillers();
      return response;
    } catch (error) {
      log.error('Error fetching popular billers:', error);
      return [];
    }
  };

  useEffect(() => {
    const fetchBillers = async () => {
      const popularBillers = await getPopularBillers();
      setBillers(popularBillers);
      setHasFetched(true);
    };
    fetchBillers();
  }, []);

  const { billersWithLogos, isLoading, error } = useBillersWithLogos(billers);

  const initialBillers = billersWithLogos.slice(0, MAX_BILLERS);
  const paddedBillers = React.useMemo(() => {
    return initialBillers.length < MAX_BILLERS
      ? [...initialBillers, ...fallbackBillers.slice(0, MAX_BILLERS - initialBillers.length)]
      : initialBillers;
  }, [initialBillers, MAX_BILLERS]);

  const allLogosLoaded = React.useMemo(() => {
    return !isLoading && paddedBillers.every((biller: Biller) => !!biller.logoKey);
  }, [isLoading, paddedBillers]);

  if (error) {
    return (
      <div className={classes.centered}>
        <ZeroStateView
          description='Bill Connect hit a snag. Please try again.'
          qIllustration='planeCrash'
          button='Retry'
          buttonVariant='contained'
          onClick={getPopularBillers}
        />
      </div>
    );
  }

  if (!hasFetched || !allLogosLoaded) {
    return (
      <div className={classes.centered}>
        <CircularProgress size={150} />
      </div>
    );
  }

  if (billersWithLogos.length === 0) {
    return (
      <div className={classes.centered}>
        <>
          <Typography variant='paragraphRegular'>No popular billers found</Typography>
          <Button onClick={getPopularBillers}>Retry</Button>
        </>
      </div>
    );
  }

  return (
    <div className={classes.billerSpace}>
      {paddedBillers.map((biller: Biller) => {
        // const isLogoValid = validImageMap[biller.id];

        return (
          <Tooltip title={biller.name} key={biller.id} arrow>
            <ButtonBase
              className={classes.billerButton}
              onClick={() => (!isLoading || !error ? handleSelect(biller) : undefined)}
            >
              <Dump obj={biller} />
              {isLoading && <Skeleton variant='rectangular' width={40} height={40} />}
              {!error && !isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <img
                    alt={biller.name}
                    src={!biller.logoKey ? DefaultLogo : biller.logoKey}
                    className={classes.logoImage}
                  />
                  <Typography className={classes.logoText} variant='paragraphRegular'>
                    {biller.name}
                  </Typography>
                </Box>
              )}
            </ButtonBase>
          </Tooltip>
        );
      })}
    </div>
  );
});
PopularBillersGrid.displayName = 'PopularBillersGrid';
